import CountrySelect from '@/components/ui/select/country-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@heidi/design-system/select';
import { TypographyP2Bold } from '@heidi/design-system/typography';
import { type LanguageCode } from '@heidi/schema/ml-scribe';
import { type CountryType } from '@heidi/utils/countries';
import { getNativeDisplayLanguageName } from '@heidi/utils/i18n';
import { FormattedMessage, useIntl } from 'react-intl';

interface DemoCountryLanguageFieldsProps {
  country: CountryType | null;
  onCountryChange: (country: CountryType) => void;
  languageCode: string | null;
  onLanguageChange: (language: string) => void;
  showLanguage: boolean;
  availableLanguages: LanguageCode[];
}

/**
 * Country + language selectors rendered at the top of every early-meeting-flow
 * variant card. Hoisting these out of the individual variants lets the user
 * change country regardless of which experience variant they're currently
 * routed to — switching country re-evaluates `useOnboardingExperience` in the
 * orchestrator and may swap them onto a different variant.
 */
export function DemoCountryLanguageFields({
  country,
  onCountryChange,
  languageCode,
  onLanguageChange,
  showLanguage,
  availableLanguages,
}: DemoCountryLanguageFieldsProps) {
  const intl = useIntl();

  return (
    <div className="flex gap-4">
      <div className="flex flex-1 flex-col gap-1">
        <TypographyP2Bold>
          <FormattedMessage id="vONi+OMghn" defaultMessage="Country" />
        </TypographyP2Bold>
        <CountrySelect value={country ?? undefined} setValue={onCountryChange} />
      </div>
      {showLanguage && (
        <div className="flex flex-1 flex-col gap-1">
          <TypographyP2Bold>
            <FormattedMessage id="y1Z3orIe9Z" defaultMessage="Language" />
          </TypographyP2Bold>
          <Select value={languageCode ?? ''} onValueChange={onLanguageChange}>
            <SelectTrigger>
              <SelectValue
                placeholder={intl.formatMessage({
                  id: 'MbU2YpE+7w',
                  defaultMessage: 'Select',
                  description: 'Placeholder for the display language select',
                })}
              />
            </SelectTrigger>
            <SelectContent>
              {availableLanguages.map((code) => (
                <SelectItem key={code} value={code}>
                  {getNativeDisplayLanguageName(code)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}
